import React from 'react';
import { DollarSign, FileBarChart } from 'lucide-react';
import PropertyCard from './ui/PropertyCard';
import DataItem from './ui/DataItem';
import Alert from '@mui/material/Alert';

/**
 * Componente para visualização de dados de faturamento relacionados à propriedade
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.propertyData - Dados da propriedade
 * @param {Function} props.onHighlightGeometry - Função para destacar geometria no mapa
 * @returns {JSX.Element} Componente de visualização de dados de faturamento
 */
const FaturamentoTab = ({ propertyData, _onHighlightGeometry }) => {
  // Extrair dados de faturamento se existirem
  const faturamentoData = propertyData?.properties?.faturamento || {};

  return (
    <div className="space-y-4">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <DollarSign className="text-emerald-600 mr-2" size={20} />
          <h3 className="text-lg font-medium text-emerald-800">Faturamento</h3>
        </div>
      </div>

      {/* Conteúdo principal */}
      <PropertyCard subtitle="Informações Financeiras" color="emerald">
        {Object.keys(faturamentoData).length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Não há informações de faturamento disponíveis para esta propriedade.
          </Alert>
        ) : (
          <div className="space-y-4">
            {/* Resumo financeiro */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <DataItem
                label="Faturamento Anual"
                value={faturamentoData.faturamento_anual || 'R$ 0,00'}
              />
              <DataItem
                label="Faturamento Mensal"
                value={faturamentoData.faturamento_mensal || 'R$ 0,00'}
              />
              <DataItem
                label="Última Atualização"
                value={faturamentoData.ultima_atualizacao || 'N/A'}
              />
            </div>

            {/* Histórico de faturamento */}
            <div className="mt-4">
              <h4 className="text-md font-medium mb-2 flex items-center">
                <FileBarChart size={16} className="mr-1.5 text-emerald-600" />
                <span>Histórico de Faturamento</span>
              </h4>

              {faturamentoData.historico && faturamentoData.historico.length > 0 ? (
                <div className="space-y-2">
                  {faturamentoData.historico.map((item, index) => (
                    <div key={index} className="p-3 border rounded-md">
                      <div className="flex justify-between">
                        <span className="font-medium">
                          {item.periodo || 'Período não especificado'}
                        </span>
                        <span className="text-emerald-600 font-medium">
                          {item.valor || 'R$ 0,00'}
                        </span>
                      </div>
                      <p className="text-sm mt-1 text-gray-600">{item.observacao || ''}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">Nenhum histórico de faturamento disponível.</p>
              )}
            </div>

            {/* Notas fiscais recentes */}
            <div className="mt-4">
              <h4 className="text-md font-medium mb-2">Notas Fiscais Recentes</h4>
              {faturamentoData.notas_fiscais && faturamentoData.notas_fiscais.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Número
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Data
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Valor
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {faturamentoData.notas_fiscais.map((nota, index) => (
                        <tr key={index}>
                          <td className="px-3 py-2 whitespace-nowrap text-sm">
                            {nota.numero || 'N/A'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm">
                            {nota.data || 'N/A'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm">
                            {nota.valor || 'R$ 0,00'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                              ${
                                nota.status === 'Paga'
                                  ? 'bg-green-100 text-green-800'
                                  : nota.status === 'Pendente'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {nota.status || 'N/A'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500">Nenhuma nota fiscal recente encontrada.</p>
              )}
            </div>
          </div>
        )}
      </PropertyCard>
    </div>
  );
};

export default FaturamentoTab;
